import type { ProjectFunnelDto, ProjectStatus } from '../api.ts';
import { addDays, buildFunnel, type FunnelDto, type FunnelInput, type FunnelPhaseKey, type FunnelPlan, type MarkEstimate } from '../funnel.ts';
import { BUILDER_END, isAside, onBoard, type BuilderTree } from './builder.ts';
import type { StepInfo } from './client.ts';
import type { ProcessStep, ProjectProcess } from './graph.ts';
import { phasesOf } from './phases.ts';
import type { Schedule } from './schedule.ts';
import type { ProcessTreeDoc } from './schema.ts';
import { projectPhases } from './standing.ts';
import { titleCase } from '../titleCase.ts';

/**
 * What the funnel reads of a job: its marks, in the order the schedule reaches
 * them, and its phases, the tree's (Jason, 2026-10-06, option A: "phases and
 * what closes each come from your tree ... The funnel's marks are those
 * closers plus milestones").
 *
 * - The marks: the steps that close a phase, the milestones (never the start
 *   or the end), and the steps that deliver an estimate (design study 17), each
 *   with the estimate set on it so far; the same for NSB and the client, each
 *   by the step's one name (design study 31, Settled 3; Jason, 2026-10-29:
 *   "i like showing major gates and milestones but we want to keep the funnel
 *   lightweight").
 * - Each phase the job has steps in ends at the step that closes it, where the
 *   job has it, else at its last step in the order of work (the prototype
 *   funnel's rule); one with no step closing it is passed once every step in
 *   it is done.
 * - The price narrows to a phase's share once it has ended: a mark's share is
 *   the least of the phases ended by the time the schedule reaches it.
 *
 * Only a plan someone made dates anything: the schedule here gives the order.
 * Each phase carries its share of the time, which places on the funnel the gate
 * closing it while nobody has dated it (`buildFunnel`; Jason, 2026-10-07).
 * A job completed or archived ended on its last step's day (`jobFinishedOn`,
 * given its `status`), and its funnel is drawn from its start to then (L-21).
 */
export function funnelFacts(
  doc: ProcessTreeDoc,
  p: ProjectProcess,
  sch: Schedule,
  o: {
    staff: boolean;
    today: string;
    info: (id: string) => StepInfo | undefined;
    plans: ReadonlyMap<string, readonly FunnelPlan[]>;
    /** The latest estimate set on each step, by the step's id. */
    estimates?: ReadonlyMap<string, MarkEstimate>;
    /** The job's status: completed or archived, it ended on its last step's day (`jobFinishedOn`). */
    status?: ProjectStatus;
  },
): Pick<FunnelInput, 'gates' | 'phases' | 'finishedOn'> {
  const live = p.steps.filter((s) => !s.off && sch.timing.has(s.id));
  const order = (a: ProcessStep, b: ProcessStep) => sch.timing.get(a.id)!.finish.localeCompare(sch.timing.get(b.id)!.finish) || sch.rank.get(a.id)! - sch.rank.get(b.id)!;
  const passedOn = (s: ProcessStep) => (s.state === 'done' ? (s.doneOn ?? o.today) : null);
  // A planned date is a gate's or a milestone's (design study 31, Settled 3); one kept on a step of work places nothing.
  const plansOf = (s: ProcessStep) => (s.kind === 'gate' || s.kind === 'milestone' ? [...(o.plans.get(s.id) ?? [])] : []);
  // Each by the step's one name, in Title Case as every step's name is (Jason, 2026-10-06), for NSB and the client alike.
  const words = (s: ProcessStep) => titleCase(s.title);
  const byKey = new Map(doc.nodes.map((n) => [n.key, n]));
  const delivers = (s: ProcessStep) => s.key !== null && Boolean(byKey.get(s.key)?.deliversEstimate);

  const phases = phasesOf(doc);
  const isEnd = (s: ProcessStep) => s.key !== null && (s.key === doc.start || s.key === BUILDER_END);
  const closers = new Set(phases.flatMap((ph) => (ph.closedBy ? [ph.closedBy] : [])));
  const phaseOf = projectPhases(doc, p, null);
  // A step's tasks go with it; its start and end are in no phase's way.
  const steps = live.filter((s) => s.parentId === null && !isEnd(s));
  const ending = phases.flatMap((ph) => {
    const members = steps.filter((s) => phaseOf.get(s.id) === ph.key).sort((a, b) => sch.rank.get(a.id)! - sch.rank.get(b.id)!);
    const closer = ph.closedBy ? live.find((s) => s.key === ph.closedBy) : undefined;
    if (!closer && !members.length) return [];
    const step = closer ?? members.at(-1)!;
    const done = closer ? passedOn(closer) : members.every((s) => s.state === 'done') ? members.map((s) => passedOn(s)!).reduce((a, b) => (a > b ? a : b)) : null;
    return [{ ph, step, passedOn: done }];
  });
  const marks = live
    .filter((s) => (s.key !== null && closers.has(s.key)) || (s.kind === 'milestone' && !isEnd(s)) || (delivers(s) && !isEnd(s)))
    .sort(order);
  const finishedOn = jobFinishedOn(doc, p, o.status);
  return {
    ...(finishedOn ? { finishedOn } : {}),
    gates: marks.map((s) => ({
      key: s.id,
      mark: s.kind === 'gate' ? 'gate' : 'ring',
      name: words(s),
      // NSB reads its own note; a client what the tree says to them, never NSB's.
      note: o.staff ? (o.info(s.id)?.why ?? null) : (o.info(s.id)?.clientNote ?? null),
      passedOn: passedOn(s),
      plans: plansOf(s),
      sureAfter: Math.min(1, ...ending.filter((e) => e.step.id === s.id || order(e.step, s) < 0).map((e) => e.ph.sureAfter)),
      deliversEstimate: delivers(s),
      estimate: o.estimates?.get(s.id) ?? null,
    })),
    phases: ending.map((e) => ({
      key: e.ph.key,
      name: e.ph.name,
      what: e.ph.what,
      sureAfter: e.ph.sureAfter,
      ...(e.ph.share !== undefined ? { share: e.ph.share } : {}),
      end: { key: e.step.id, name: words(e.step), passedOn: e.passedOn, plans: plansOf(e.step) },
    })),
  };
}

/**
 * The day a job completed or archived ended (L-21, option A as Jason chose it, 2026-10-08: "a completed job ends on the
 * day its last step was done"): the latest day a step of it was done, setting it up and archiving it aside (the tree's start and end are
 * NSB's own acts, done the day they are, not the work), else theirs; null while it runs, or when nothing was done. A
 * cancelled job ended the day it was cancelled, which the project says (the funnel's `endedOn`), and that day stands
 * before this one, even once it is archived.
 */
export function jobFinishedOn(doc: Pick<ProcessTreeDoc, 'start'>, p: Pick<ProjectProcess, 'steps'>, status: ProjectStatus | undefined): string | null {
  if (status !== 'closed' && status !== 'archived') return null;
  const done = p.steps.filter((s) => !s.off && s.state === 'done' && s.doneOn !== null);
  const isEnd = (s: ProcessStep) => s.key !== null && (s.key === doc.start || s.key === BUILDER_END);
  const last = (xs: ProcessStep[]) => xs.map((s) => s.doneOn!).reduce<string | null>((a, b) => (a === null || b > a ? b : a), null);
  return last(done.filter((s) => !isEnd(s))) ?? last(done);
}

/** The prototypical job's time, the whole of it, on the funnel's calendar: a thousand days, so a day is a tenth of a percent. Never shown as days. */
export const PROTOTYPE_DAYS = 1000;
/** The day its time starts from, an axis's zero; never shown as a date. */
const PROTOTYPE_START = '2000-01-01';
/** The middle of its price, which its spread is drawn round; never shown as money. */
export const PROTOTYPE_MIDDLE_CENTS = 1_000_000;
/** A point of the prototypical job's band as the spread either way it draws: 0.45 is ±45%. */
export const prototypeSpread = (r: { highCents: number }): number => Math.max(0, (r.highCents - PROTOTYPE_MIDDLE_CENTS) / PROTOTYPE_MIDDLE_CENTS);
/** Where a day of the prototypical job falls in its time, 0 to 100. */
export const prototypePercent = (f: Pick<FunnelDto, 'startOn' | 'endOn'>, on: string): number => {
  const span = Date.parse(`${f.endOn}T00:00:00Z`) - Date.parse(`${f.startOn}T00:00:00Z`);
  return span > 0 ? ((Date.parse(`${on}T00:00:00Z`) - Date.parse(`${f.startOn}T00:00:00Z`)) / span) * 100 : 0;
};

/**
 * The master's prototypical job, as a job's funnel is drawn (L-12; Jason, 2026-10-07: "make sure this funnel is built
 * the same way the real funnel is but you can use the percentages of 100% for the phases, and the confidence levels as
 * shown"): the tree's phases, in order, each closed by its gate where the board has it, put through `buildFunnel` as a
 * job's are on its first day with nothing dated, so each closing gate is placed by the phases' shares of the time as a
 * job's undated gates are, across the whole of the job's time (the frame given: a hundred percent, never weeks or
 * dates). A phase with no share takes no time. Its band is how sure the price is, as set on the tree, in place of the
 * estimates a job has: the starting spread at the start and each phase's (`sureAfter` of the starting spread, never
 * widening again) where it closes, straight from one to the next, round a middle that is never shown. Nothing on it is
 * done or next, nothing is billed, and it has no budget, no seasons and no schedule: the master has none of those.
 * With no share on any phase there is nothing to lay out, and the band is empty.
 */
export function prototypeFunnel(t: Pick<BuilderTree, 'phases' | 'startSpread' | 'nodes'>): ProjectFunnelDto {
  const on = onBoard(t);
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const closerOf = (key: string | null) => {
    const n = key !== null ? byKey.get(key) : undefined;
    return n && on.has(n.key) && n.parent === null && !isAside(n) ? n : null;
  };
  const phases = t.phases.map((ph) => ({ ph, closer: closerOf(ph.closedBy) }));
  const end = addDays(PROTOTYPE_START, PROTOTYPE_DAYS);
  const funnel = buildFunnel({
    today: PROTOTYPE_START,
    startedOn: PROTOTYPE_START,
    endedOn: null,
    budget: null,
    estimates: [],
    cap: null,
    frame: { from: PROTOTYPE_START, to: end },
    gates: phases.flatMap(({ ph, closer }) =>
      closer
        ? [
            {
              key: closer.key,
              name: titleCase(closer.title),
              note: closer.note?.trim() || null,
              passedOn: null,
              plans: [],
              mark: closer.kind === 'gate' ? ('gate' as const) : ('ring' as const),
              sureAfter: ph.sureAfter,
              deliversEstimate: closer.deliversEstimate ?? false,
              estimate: null,
            },
          ]
        : [],
    ),
    // A draft's phases are keyed by the names they were made with: read here only as keys.
    phases: phases.map(({ ph, closer }) => ({
      key: ph.key as FunnelPhaseKey,
      name: ph.name,
      what: ph.what,
      sureAfter: ph.sureAfter,
      share: ph.share ?? 0,
      end: { key: closer?.key ?? `phase:${ph.key}`, name: closer ? titleCase(closer.title) : ph.name, passedOn: null, plans: [] },
    })),
    spent: [],
  });
  // Each phase ends where the shares put it, the same rule that placed its gate; its spread there, never widening.
  const total = phases.reduce((sum, { ph }) => sum + (ph.share ?? 0), 0);
  let along = 0;
  let spread = t.startSpread;
  const at = (s: number) => ({ lowCents: Math.max(0, Math.round(PROTOTYPE_MIDDLE_CENTS * (1 - s))), highCents: Math.round(PROTOTYPE_MIDDLE_CENTS * (1 + s)) });
  const ends = phases.map(({ ph }) => {
    along += ph.share ?? 0;
    spread = Math.min(spread, t.startSpread * ph.sureAfter);
    return { on: addDays(PROTOTYPE_START, Math.round((PROTOTYPE_DAYS * along) / Math.max(1, total))), range: at(spread) };
  });
  const band = total > 0 ? [{ on: PROTOTYPE_START, range: at(t.startSpread) }, ...ends].filter((c, i, all) => all[i + 1]?.on !== c.on).map((c) => ({ on: c.on, ...c.range, projected: false })) : [];
  const rangeAt = new Map(phases.map(({ closer }, i) => [closer?.key ?? '', ends[i]!.range]));
  return {
    ...funnel,
    band,
    gates: funnel.gates.map((g) => ({ ...g, state: 'upcoming' as const, range: g.on ? (rangeAt.get(g.key) ?? null) : null })),
    phases: funnel.phases.map((p, i) => ({
      ...p,
      startOn: total > 0 ? (ends[i - 1]?.on ?? PROTOTYPE_START) : null,
      endOn: total > 0 ? ends[i]!.on : null,
      placed: true,
      state: 'ahead' as const,
      range: total > 0 ? ends[i]!.range : null,
    })),
    prototype: true,
    budget: null,
    ceiling: null,
    seasons: { across: [], down: [] },
    seasonsPainting: false,
  };
}
